import React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import DOMPurify from 'dompurify';
import {
  openPreviousConversationsSection,
  openConversationAttachmentsSection,
  setShowTools as showToolsAction,
  clearQueue,
  setDebugModeEnabled,
} from '../../../actions/index';
import { collectAndHydrateTicketsOnSameChannel } from '../../../actions/ticketActions';
import { hideConversation, unhideConversation, showNudgePopup, setBtnActivity, setShowSpecialistTools } from 'actions';
import { fetchDefaultEscalationMessage } from 'actions/creatorsAsync';
import { fetchBraintrustConfig } from '../../../actions/braintrustActions';
import { showErrorMessage } from '../../../util/errorPopupUtil';
import { isLiveChat } from 'util/chat';
import * as constants from '../../../constants/ticketStatuses';
import { TicketTextBold } from '../../common/fontElements';
import { determineTicketSubject, setAnchorElementsTarget } from '../../../util';
import { makeDisplayName } from 'util/formatUtil';
import ExpiryTimer from '../../inboxPanel/ExpiryTimer/ExpiryTimer';
import { getExpiryTimeForTicket, showTicketExpiryTimer } from 'util/timerUtil';
import ConversationRating from '../../ConversationRating/ConversationRating';
import LanguageSwitch from 'components/Messages/LanguageSwitch/LanguageSwitch';
import uniq from 'lodash/uniq';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import filter from 'lodash/filter';
import flatten from 'lodash/flatten';

import {
  extractSelectedTicketFromState,
  extractSelectedInboxTab,
  extractMlOrdersIdsDataFromState,
  isSelectedTicketReviewTicket,
  isSpecialistLite,
  hasSelectedExpertForForwarding,
  extractSelectedHistoricalTicketIdFromState,
  extractShowSpecialistTools,
  getSimplrTaggingActive,
  selectIsTicketTranslated,
  getGreyOutFeatureEnabled,
} from 'extractFromState';
import { ASSIGNED_TAB } from '../../../constants/ui';
import get from 'lodash/get';
import { ErrorStrings } from '../../../constants/fixedMessages';
import { twoBtnPopupCreator } from '../../popups/ConfirmPopup/helper';
import ForwardToExpertComponent from 'components/ForwardToExpert/ForwardToExpert';
import { BUTTON_IDS } from 'reducers/ui/buttons';
import { INTERNAL_NOTE } from '../../../constants/messageTypes';
import { INTERNAL } from '../../../constants/messageDirection';
import { SPECIALIST_ONLY_HINT, ESCALATION_REASON, HINT } from '../../../constants/noteTypes';
import TicketIcon from '../../TicketIcon/TicketIcon';
import {
  ErrorMessage,
  FieldWrapper,
  Container,
  ExpertTools,
  SpecialistTools,
  PartnerProtectorTools,
  CloseContainer,
  ToolPlaceholder,
  TicketTextBoldBlue,
  TicketTextBoldWhite,
  ToolSection,
  MessageTypeImage,
  MessageLeftDetails,
  MessageRightDetails,
  TicketDateText,
  FromDetails,
  TicketInfo,
  GrayTicketText,
  SelectHolder,
  ExpertReviewIcon,
  Button,
  AttachmentsPlaceholder,
  TicketTextBoldGray,
  MessageCounter,
  Divider,
} from './SelectedMessage.styles';
import MessageExternalLinks from '../MessageExternalLinks/MessageExternalLinks';
import PreviousConversationSection from '../../PreviousConversationSection/PreviousConversationSection';
import * as ErrorService from 'services/ErrorService/ErrorService';
import ConversationAttachmentsSection from '../../common/ConversationAttachmentsSection/ConversationAttachmentsSection';
import { isAPartnerProtector } from 'extractFromState';
import BaseSelect from 'components/common/BaseSelect/BaseSelect';
import UpsellIcon from 'components/common/UpsellIcon/UpsellIcon';
import environment from 'environment';
import makeRequest from 'api/makeRequest';

const handleFullStoryButtonClick = (expertEmail, conversationId) => {
  const fullStoryLink = `https://app.fullstory.com/ui/7SCA2/segments/everyone/people:search:((NOW%2FDAY-29DAY:NOW%2FDAY%2B1DAY):((UserEmail:==:"${expertEmail}")):():(((EventType:==:"custom"):(EventCustomName:==:"SEND_CLICK"):(evt_conversationId_str:==:"${conversationId}"))):():)/0`;
  window.open(fullStoryLink, '_blank', 'noopener');
};

const getUniqueNameEmailPairFromConversation = (names, emails) => {
  const result = [];
  for (let i = 0; i < names.length; i++) {
    result.push({
      value: emails[i],
      label: [names[i]],
    });
  }

  return result;
};

// Populates the expert dropdown in the quality rating component
// Don't include experts associated only with a message which is an internal note
const getNameIdPairForEachUniqueId = (conversation, { includeBots } = {}) => {
  const filteredEmails = conversation.allMessages.filter((messageId) => {
    return includeBots
      ? conversation[messageId].expert
      : conversation[messageId].expert && !conversation[messageId].expert.isSystemExpert;
  });

  const seenIds = {};
  const nameIdPairs = filteredEmails.map((messageId) => {
    const id = conversation[messageId].expert.simplrExpertId;
    const isInternalNote = conversation[messageId].messageType === INTERNAL_NOTE;
    const isHint = conversation[messageId].noteType === HINT;
    const isSpecialistOnlyHint = conversation[messageId].noteType === SPECIALIST_ONLY_HINT;
    const isEscalationReason = conversation[messageId].noteType === ESCALATION_REASON;
    if (id in seenIds || isInternalNote || isHint || isSpecialistOnlyHint || isEscalationReason) {
      return null;
    }
    seenIds[id] = id;
    const firstName = conversation[messageId].expert.firstName;
    const lastName = conversation[messageId].expert.lastName;

    return { value: id, label: makeDisplayName({ id, firstName, lastName }) };
  });

  return compact(uniq(nameIdPairs));
};

const getUniqueExpertEmailsFromConversation = (conversation) => {
  const filteredEmails = conversation.allMessages.filter((messageId) => {
    return conversation[messageId].expert && !conversation[messageId].expert.isSystemExpert;
  });
  const emails = filteredEmails.map((messageId) => {
    return conversation[messageId].expert.email;
  });

  return compact(uniq(emails));
};

const getUniqueExpertNamesFromConversation = (conversation) => {
  const filteredNames = conversation.allMessages.filter((messageId) => {
    return conversation[messageId].expert && conversation[messageId].expert.firstName !== 'Simplr';
  });
  const names = filteredNames.map((messageId) => {
    const firstName = conversation[messageId].expert.firstName;
    const lastName = conversation[messageId].expert.lastName;
    const id = conversation[messageId].expert.simplrExpertId;

    return makeDisplayName({ id, firstName, lastName });
  });

  return compact(uniq(names));
};

const getConversationMediaUrls = (conversation) => {
  const allMessages = filter(conversation, (message) => {
    return message?.messageType !== INTERNAL_NOTE && message?.mediaUrl;
  });
  const mediaUrls = allMessages.map((message) => message.mediaUrl);

  return uniq(flatten(mediaUrls));
};

class SelectedMessage extends React.Component {
  componentDidMount() {
    if (this.props.isSelectedTicketLiveChat) {
      this.props.loadDefaultEscalationMessage();
    }

    if (this.props.isAdmin) {
      this.props.fetchBraintrustConfig(this.props.selectedTicket?.simplrAccountPtrId);
    }
  }

  componentDidUpdate(prevProps) {
    const hasTicketChanged = get(this.props, 'selectedTicket.ticketId') !== get(prevProps, 'selectedTicket.ticketId');
    if (hasTicketChanged && this.props.isSelectedTicketLiveChat) {
      this.props.loadDefaultEscalationMessage();
    }

    if (hasTicketChanged && this.props.isAdmin) {
      this.props.fetchBraintrustConfig(this.props.selectedTicket?.simplrAccountPtrId);
    }
  }

  shouldShowConversationRatingTool = () => {
    const { isAdmin, isHistoricalTicket, selectedTab, reviewTicketSelected } = this.props;

    if (isAdmin) {
      return !isHistoricalTicket && selectedTab !== ASSIGNED_TAB;
    }

    if (this.props.isAPartnerProtector) {
      return !isHistoricalTicket && reviewTicketSelected;
    }

    return false;
  };

  shouldDisplaySpecialistTools() {
    const { isAdmin, isSelectedHistoricalTicket, selectedTab } = this.props;

    return isAdmin && !this.props.isSpecialistLite && !isSelectedHistoricalTicket && selectedTab !== 'assigned';
  }

  // used to temporarily disable button
  getForwardButtonId() {
    const { allForwardBtn, escalateForwardBtn } = BUTTON_IDS;
    if (this.props.selectedTab === 'all') {
      return allForwardBtn;
    }

    return escalateForwardBtn;
  }

  handleBraintrustClick = async (e) => {
    e.preventDefault();
    const { ticket } = this.props;

    try {
      const data = await makeRequest(`${environment.app.serverApiUrl}/v1/braintrust/getRedirectUrl`, {
        params: { conversationId: ticket.ticketId },
      });

      if (data && data.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      showErrorMessage(error, true);
    }
  };

  renderAttachmentsBox = (attachmentUrls) => {
    return this.props.conversationAttachmentsSection ? (
      <ConversationAttachmentsSection mediaUrls={attachmentUrls} />
    ) : (
      <AttachmentsPlaceholder
        onClick={() => {
          this.props.openConversationAttachmentsSection();
        }}
      >
        <TicketTextBoldGray>Attachments ({attachmentUrls.length})</TicketTextBoldGray>
        <img alt="hide" src="/images/arrows/down-arrow-gray.svg" />
      </AttachmentsPlaceholder>
    );
  };

  render() {
    const {
      isAdmin,
      ticket,
      showEmailToExperts,
      conversation,
      mlOrdersIds,
      selectedTicket,
      reviewTicketSelected,
      validationTicketSelected,
      simplrTaggingActive,
      hasExpertSelectedInForwarding,
      btnActivity,
      showSpecialistTools,
      selectedTab,
      isTicketTranslated,
      greyOutFeatureEnabled,
      isSelectedHistoricalTicket,
      isBraintrustEnabled,
    } = this.props;

    if (!ticket) {
      showErrorMessage(new Error(ErrorStrings.SELECTED_TICKET_NOT_FOUND), true);

      return null;
    }

    const expertsEmails = getUniqueExpertEmailsFromConversation(conversation);
    const expertsNames = getUniqueExpertNamesFromConversation(conversation);
    const fullStoryObject = getUniqueNameEmailPairFromConversation(expertsNames, expertsEmails);
    const expertsObjectForRatingSelect = getNameIdPairForEachUniqueId(conversation, { includeBots: isAdmin });
    const formattedConversationUpdatedAt =
      ticket.conversationUpdatedAt && moment(ticket.conversationUpdatedAt).format('MM/DD/YY h:mm a');

    let subject = determineTicketSubject(ticket);
    subject = DOMPurify.sanitize(subject);
    const orderIdsMentionedInTickets = mlOrdersIds && mlOrdersIds.orderIdsMentionedInTickets;
    const conversationMediaUrls = getConversationMediaUrls(conversation);

    // adds a highlighter for every order # found by the ml service
    if (orderIdsMentionedInTickets) {
      orderIdsMentionedInTickets.forEach((orderId) => {
        if (!subject) {
          ErrorService.handleError({
            error: Error('variable undefined in SelectedMessage'),
            customFields: {
              orderId,
            },
          });

          return subject;
        }

        return (subject = subject.replace(
          new RegExp(orderId, 'ig'),
          `<strong id="orderId" data-value="${orderId}">${orderId}</strong>`
        ));
      });
    }

    let showForward = '';
    let showHide = '';
    let showUnhide = '';
    let showNudge = '';
    let showUnstick = '';

    // TO DO: we need to sort out the duplicate cases in this switch statement
    // the entire class-based system should probably be replaced
    if (selectedTicket && selectedTicket.ticketStatus) {
      /* eslint-disable no-duplicate-case */
      switch (ticket.ticketStatus) {
        case constants.TICKET_STATUS_NOMINATED:
          showForward = 'hide';
          showUnhide = 'hide';
          break;
        case constants.TICKET_STATUS_AWAITING_SPECIALIST:
        case constants.TICKET_STATUS_AWAITING_NOMINATION:
        case constants.TICKET_STATUS_NOMINATED_FROM_ESCALATED:
        case constants.TICKET_STATUS_ESCALATED:
          showUnhide = 'hide';
          break;
        case constants.TICKET_STATUS_AWAITING_SPECIALIST:
          showForward = '';
          showUnstick = '';
          break;
        case constants.TICKET_STATUS_HIDDEN:
          showHide = 'hide';
          showUnhide = '';
          showForward = 'hide';
          break;
        case constants.TICKET_STATUS_ASSIGNED:
          showForward = 'hide';
          showHide = 'hide';
          showUnhide = 'hide';
          showUnstick = '';
          break;
        case constants.TICKET_STATUS_SELF_HELP:
          showForward = '';
          showHide = '';
          showUnhide = 'hide';
          showUnstick = '';
          break;
        default:
          showForward = 'hide';
          showHide = 'hide';
          showUnhide = 'hide';
          showUnstick = 'hide';
        /* eslint-disable no-duplicate-case */
      }
    }
    if (this.props.isHistoricalTicket) {
      showForward = 'hide';
      showHide = 'hide';
      showUnhide = 'hide';
      showNudge = 'hide';
    }

    if (hasExpertSelectedInForwarding) {
      showHide = 'hide';
      showUnhide = 'hide';
      showNudge = 'hide';
      showUnstick = 'hide';
    }

    if (simplrTaggingActive) {
      showForward = 'hide';
    }

    const isUpsellTicket = !!ticket.moreTicketInfo?.humanAndBotBothActiveInChat;

    const messageCount = conversation.allMessages.filter((messageId) => {
      const message = conversation[messageId];

      return message.direction !== INTERNAL && message.messageType !== INTERNAL_NOTE;
    }).length;

    return (
      <Container>
        <ExpertTools isSelectedHistoricalTicket={this.props.isSelectedHistoricalTicket}>
          <MessageTypeImage>
            <TicketIcon ticket={ticket} />
          </MessageTypeImage>
          <MessageLeftDetails>
            <FromDetails>
              <TicketInfo>{ticket.simplrTicketNumber}</TicketInfo>
              <Divider />
              <TicketInfo>{ticket.platformTicketId}</TicketInfo>
              <Divider />
              <MessageCounter>
                {messageCount} {messageCount === 1 ? 'MESSAGE' : 'MESSAGES'}
              </MessageCounter>
            </FromDetails>
            <GrayTicketText dangerouslySetInnerHTML={{ __html: setAnchorElementsTarget(subject) }} />
            {ticket.customerEmail && showEmailToExperts && <GrayTicketText>{ticket.customerEmail}</GrayTicketText>}
            <MessageExternalLinks
              ticket={ticket}
              isAdmin={isAdmin}
              isBraintrustEnabled={isBraintrustEnabled}
              onBraintrustClick={this.handleBraintrustClick}
            />
          </MessageLeftDetails>
          <MessageRightDetails>
            <FieldWrapper>
              {isUpsellTicket && <UpsellIcon />}
              <TicketTextBold>{ticket.brand || ticket.smbName}</TicketTextBold>
            </FieldWrapper>
            <TicketDateText>{formattedConversationUpdatedAt}</TicketDateText>
            {showTicketExpiryTimer(ticket, isAdmin) && (
              <ExpiryTimer {...ticket} ticketExpiresIn={getExpiryTimeForTicket(ticket)} />
            )}
            {!isAdmin && ticket.escalatedForReview && selectedTab === 'assigned' && (
              <ExpertReviewIcon src="/images/new-designs/expert-review-tag.svg" />
            )}
          </MessageRightDetails>
        </ExpertTools>
        {this.shouldDisplaySpecialistTools() && (
          <>
            {showSpecialistTools ? (
              <SpecialistTools>
                <ToolSection>
                  <SelectHolder>
                    <BaseSelect
                      defaultSelect={false}
                      isSearchable
                      isClearable
                      label={'FullStory links'}
                      placeholder={'Select a FullStory'}
                      value={''}
                      options={fullStoryObject}
                      onChange={(option) =>
                        handleFullStoryButtonClick(option.value, conversation.selectedConversationId)
                      }
                      noOptionsMessage={() => 'No results found'}
                    />
                  </SelectHolder>
                  <ErrorService.ErrorBoundary
                    customFields={{ system: 'Conversation Rating' }}
                    fallback={<ErrorMessage>Something went wrong.</ErrorMessage>}
                  >
                    <ConversationRating ticket={ticket} expertsObject={expertsObjectForRatingSelect} />
                  </ErrorService.ErrorBoundary>
                  <Button
                    label="UNSTUCK"
                    onClick={() => {
                      twoBtnPopupCreator({
                        content: {
                          main: `Are you sure you want to unstick this ticket?`,
                          iconPath: '/images/green-alert-bell.svg',
                        },
                        btnConfig: {
                          dismiss: 'DISMISS',
                          confirm: 'CONFIRM',
                        },
                        onConfirm: () => {
                          this.props.clearStuckTicketsFromExpertQueue(conversation);
                        },
                      });
                    }}
                    className={showUnstick}
                  />
                  <Button
                    label="NUDGE"
                    disabled={simplrTaggingActive}
                    onClick={() => this.props.showNudgePopup()}
                    className={showNudge}
                  />
                  <ForwardToExpertComponent
                    className={showForward}
                    setBtnActivity={this.props.setBtnActivity}
                    isButtonActive={!simplrTaggingActive && btnActivity[this.getForwardButtonId()]}
                  />
                  <Button
                    label="HIDE"
                    className={showHide}
                    disabled={simplrTaggingActive}
                    onClick={() => {
                      twoBtnPopupCreator({
                        content: {
                          main: `Are you sure you want to hide this ticket?`,
                          iconPath: '/images/green-alert-bell.svg',
                        },
                        btnConfig: {
                          dismiss: 'DISMISS',
                          confirm: 'CONFIRM',
                        },
                        onConfirm: () => {
                          this.props.hideConversation(selectedTicket.ticketId);
                        },
                      });
                    }}
                  />
                  <Button
                    label="UNHIDE"
                    className={showUnhide}
                    disabled={simplrTaggingActive}
                    onClick={() => this.props.unhideConversation(ticket.ticketId)}
                  />
                  <Button
                    label={`DEBUG MODE ${this.props.isDebugModeEnabled ? 'ON' : 'OFF'}`}
                    fullColored={this.props.isDebugModeEnabled}
                    onClick={() => this.props.setDebugModeEnabledValue(!this.props.isDebugModeEnabled)}
                  />
                </ToolSection>
                <CloseContainer>
                  <img
                    alt="hide"
                    src="/images/arrows/up-arrow-gray.svg"
                    onClick={() => this.props.setShowSpecialistTools(false)}
                  />
                </CloseContainer>
              </SpecialistTools>
            ) : (
              <ToolPlaceholder onClick={() => this.props.setShowSpecialistTools(true)}>
                <TicketTextBoldBlue>Specialist tools</TicketTextBoldBlue>
                <img alt="hide" src="/images/arrows/down-arrow-blue.svg" />
              </ToolPlaceholder>
            )}
          </>
        )}
        {!ticket.isTestTicket &&
          !validationTicketSelected &&
          (this.props.previousConversationSection ? (
            <PreviousConversationSection />
          ) : (
            <ToolPlaceholder
              onClick={() => {
                this.props.openPreviousConversationsSection();
              }}
            >
              <TicketTextBoldBlue>Previous Conversations</TicketTextBoldBlue>
              <img alt="hide" src="/images/arrows/down-arrow-blue.svg" />
            </ToolPlaceholder>
          ))}
        {!isEmpty(conversationMediaUrls) && this.renderAttachmentsBox(conversationMediaUrls)}
        {reviewTicketSelected && !isSelectedHistoricalTicket && (
          <PartnerProtectorTools>
            <TicketTextBoldWhite>Please rate this ticket:</TicketTextBoldWhite>
            <ErrorService.ErrorBoundary
              customFields={{ system: 'Conversation Rating' }}
              fallback={<ErrorMessage>Something went wrong.</ErrorMessage>}
            >
              <ConversationRating
                ticket={ticket}
                expertsObject={expertsObjectForRatingSelect}
                darkTheme={true}
                greyOutFeatureEnabled={greyOutFeatureEnabled}
              />
            </ErrorService.ErrorBoundary>
          </PartnerProtectorTools>
        )}
        {isTicketTranslated && <LanguageSwitch />}
      </Container>
    );
  }
}

const mapStateToProps = (state) => ({
  greyOutFeatureEnabled: getGreyOutFeatureEnabled(state),
  isBraintrustEnabled: state.braintrust.isBraintrustEnabled,
  isSelectedTicketLiveChat:
    !!extractSelectedTicketFromState(state) && !!isLiveChat(extractSelectedTicketFromState(state).communicationType),
  selectedTab: extractSelectedInboxTab(state),
  mlOrdersIds: extractMlOrdersIdsDataFromState(state),
  monitoredTicketIdFirstChar: state.parseConfig.monitoredTicketIdFirstChar || [],
  historicalTicketIds: state.tickets.historicalTickets.allTickets,
  historicalTickets: state.tickets.historicalTickets,
  notShowingMonitoredTicketBadgeForPartners: get(
    state.parseConfig,
    'selectedPartners.notShowingMonitoredTicketBadge',
    []
  ),
  reviewTicketSelected: isSelectedTicketReviewTicket(state),
  selectedTicket: extractSelectedTicketFromState(state),
  simplrTaggingActive: getSimplrTaggingActive(state),
  isSpecialistLite: isSpecialistLite(state),
  isAPartnerProtector: isAPartnerProtector(state),
  hasExpertSelectedInForwarding: hasSelectedExpertForForwarding(state),
  isSelectedHistoricalTicket: !!extractSelectedHistoricalTicketIdFromState(state),
  selectedHistoricalTicketId: extractSelectedHistoricalTicketIdFromState(state),
  btnActivity: state.ui.buttons.btnActivity,
  showSpecialistTools: extractShowSpecialistTools(state),
  previousConversationSection: state.ui.previousConversationSection,
  showTools: state.ui.tools.general.showTools,
  conversationAttachmentsSection: state.ui.conversationAttachmentsSection,
  isTicketTranslated: selectIsTicketTranslated(state),
  isDebugModeEnabled: state.ui.userSettings.debugModeEnabled,
});

const mapDispatchToProps = (dispatch) => ({
  async loadDefaultEscalationMessage() {
    dispatch(fetchDefaultEscalationMessage());
  },
  hideConversation(ticketId) {
    dispatch(hideConversation(ticketId));
  },
  unhideConversation(ticketId, buttonId) {
    dispatch(unhideConversation(ticketId, buttonId));
  },
  showNudgePopup() {
    dispatch(showNudgePopup(true));
  },
  setBtnActivity(buttonId, isActive) {
    dispatch(setBtnActivity(buttonId, isActive));
  },
  setShowSpecialistTools(value) {
    dispatch(setShowSpecialistTools(value));
  },
  collectAndHydrateTicketsOnSameChannel: () => {
    dispatch(collectAndHydrateTicketsOnSameChannel());
  },
  openPreviousConversationsSection() {
    dispatch(openPreviousConversationsSection());
  },
  setShowTools: (showTools) => {
    dispatch(showToolsAction(showTools));
  },
  openConversationAttachmentsSection() {
    dispatch(openConversationAttachmentsSection());
  },
  clearStuckTicketsFromExpertQueue(conversation) {
    dispatch(clearQueue(conversation));
  },
  setDebugModeEnabledValue: (isDebugModeEnabled) => {
    dispatch(setDebugModeEnabled(isDebugModeEnabled));
  },
  fetchBraintrustConfig: (simplrAccountId) => {
    dispatch(fetchBraintrustConfig(simplrAccountId));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(SelectedMessage);
